import { ExitToApp } from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import logoWhite from 'assets/images/logoProdutos/alertai_icon_full_white.svg';
import logo from 'assets/images/logoProdutos/alertai_icon_light.svg';
import logoSingleWhite from 'assets/images/logoProdutos/alertai_icon_single_full_white.svg';
import logoSingle from 'assets/images/logoProdutos/alertai_icon_single_light.svg';
import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../context/AppContextProvider';
import { useLogout } from '../../hooks/useLogout';
import { IRouteProps } from '../../routes/routes';
import { isSportsPlan } from '../../services/DefaultService';
import { fontColorDarkTheme } from '../../styles/global.styles';
import { isRouteRenderable, SidebarMenuItem } from './_components/SidebarMenuItem';
import { SidebarSectionTitle } from './_components/SidebarSectionTitle';

const ACCENT_COLOR = 'var(--app-accent)';
const ACCENT_BG = 'var(--app-accent-bg)';

interface SidebarComponentProps {
  brandName: string;
  routes: IRouteProps[];
}

const SidebarComponent = ({ routes, brandName }: SidebarComponentProps) => {
  const { pathname } = useLocation();
  const { setIsOpenedSidebar, isOpenedSidebar, isRouteAllowed, theme } = useContext(AppContext);
  const logout = useLogout();
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const routesScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef(0);

  const handleToggle = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMouseLeave = () => {
    if (routesScrollRef.current) {
      savedScrollTop.current = routesScrollRef.current.scrollTop;
    }
    setIsOpenedSidebar(false);
  };

  useLayoutEffect(() => {
    if (!isOpenedSidebar || !routesScrollRef.current) return;
    const el = routesScrollRef.current;
    const start = performance.now();
    let rafId: number;

    const tick = (now: number) => {
      el.scrollTop = savedScrollTop.current;
      if (now - start < 400) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [isOpenedSidebar]);

  return (
    <Box
      onMouseEnter={() => setIsOpenedSidebar(true)}
      onMouseLeave={handleMouseLeave}
      sx={{
        backgroundColor: 'var(--app-surface-2)',
        width: isOpenedSidebar ? '20rem' : '5rem',
        height: '100%',
        position: 'fixed',
        zIndex: 99999,
        overflow: isOpenedSidebar ? 'hidden' : 'visible',
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        p: 2,
        bottom: 0,
        top: 0,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ height: 50, flexShrink: 0 }} key="logo-container">
        {isOpenedSidebar ? (
          <img
            key="logo-expanded"
            height={30}
            src={theme === 'BLACK_WHITE' ? logoWhite : logo}
            alt="Logo"
          />
        ) : (
          <img
            key="logo-collapsed"
            height={50}
            style={{ marginTop: -5 }}
            src={theme === 'BLACK_WHITE' ? logoSingleWhite : logoSingle}
            alt="Logo reduzida"
          />
        )}
      </Box>

      {isOpenedSidebar && isSportsPlan() && (
        <Box sx={{ flexShrink: 0, mt: 0.5 }}>
          <Typography
            component="span"
            sx={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 500,
              color: ACCENT_COLOR,
              backgroundColor: ACCENT_BG,
              borderRadius: 999,
              px: 1.25,
              py: 0.25,
            }}
          >
            Plano Sports
          </Typography>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <Divider sx={{ mt: 2, width: '50%', borderColor: fontColorDarkTheme }} />
      </Box>

      <Box
        ref={routesScrollRef}
        sx={{
          mt: 1,
          flexGrow: 1,
          overflowY: isOpenedSidebar ? 'auto' : 'hidden',
          overflowX: 'hidden',
          pr: isOpenedSidebar ? 1 : 0,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.15)',
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255,255,255,0.25)',
          },
        }}
      >
        {routes.map((route, routeIndex) => {
          if (route.hideInMenu || (!route.protected && route.type !== 'title')) return null;

          if (route.type === 'title') {
            const rest = routes.slice(routeIndex + 1);
            const nextTitleOffset = rest.findIndex((r) => r.type === 'title');
            const sectionRoutes = nextTitleOffset === -1 ? rest : rest.slice(0, nextTitleOffset);
            if (!sectionRoutes.some((r) => isRouteRenderable(r, isRouteAllowed))) return null;

            return (
              <SidebarSectionTitle
                key={route.key || routeIndex}
                name={route.name ?? ''}
                isOpenedSidebar={isOpenedSidebar}
                isFirst={routeIndex === 0}
              />
            );
          }

          return (
            <SidebarMenuItem
              key={route.key || routeIndex}
              route={route}
              pathname={pathname}
              isOpenedSidebar={isOpenedSidebar}
              isExpanded={!!expandedKeys[route.key]}
              onToggle={() => handleToggle(route.key)}
            />
          );
        })}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Divider sx={{ my: 2, borderColor: fontColorDarkTheme }} />
        <Button
          variant="text"
          onClick={() => logout()}
          sx={{
            color: fontColorDarkTheme,
            width: '100%',
            minWidth: 0,
            height: 44,
            borderRadius: 2,
            p: 1.5,
            pl: isOpenedSidebar ? 2.25 : 0,
            pr: isOpenedSidebar ? 1.75 : 0,
            mb: 0.5,
            display: 'flex',
            justifyContent: isOpenedSidebar ? 'flex-start' : 'center',
            textTransform: 'none',
            transition: 'background-color 0.15s',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            '& .MuiButton-startIcon': {
              marginRight: isOpenedSidebar ? undefined : 0,
              marginLeft: isOpenedSidebar ? undefined : 0,
            },
          }}
          startIcon={<ExitToApp />}
        >
          <Typography
            sx={{
              mr: 'auto',
              ml: 0,
              fontSize: 14,
              display: isOpenedSidebar ? 'block' : 'none',
              visibility: isOpenedSidebar ? 'visible' : 'hidden',
              opacity: isOpenedSidebar ? 1 : 0,
              whiteSpace: 'nowrap',
              transition: 'visibility 0s, opacity 0.5s linear',
            }}
          >
            {t('routersNames.logout')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default SidebarComponent;